import React from "react"
import {
  IconRun,
  IconMoodSmile,
  IconSparkles,
  IconClockHour3,
  IconMapPin,
  IconUser,
  IconLink,
  IconExclamationCircle,
  IconNumbers,
  IconPointer,
  IconTag,
  IconBook2,
  type Icon as TablerIcon,
  IconMacro,
} from "@tabler/icons-react"

import HintTooltip from "./hint_tooltip"

interface PartOfSpeechMeta {
  label: string
  /** Irish name for the part of speech, shown to teach the term. */
  irish: string
  /** Plain-English "what is this?" for the tooltip. */
  description: string
  Icon: TablerIcon
}

// Central config: icon + Irish name + description per part of speech. One place to add or tweak.
export const PART_OF_SPEECH_META: Record<string, PartOfSpeechMeta> = {
  noun: {
    label: "noun",
    irish: "ainmfhocal",
    description: "A person, place, thing, or idea.",
    Icon: IconMacro,
  },
  verb: {
    label: "verb",
    irish: "briathar",
    description: "An action or a state of being.",
    Icon: IconRun,
  },
  adjective: {
    label: "adjective",
    irish: "aidiacht",
    description: "A word that describes a noun.",
    Icon: IconSparkles,
  },
  adverb: {
    label: "adverb",
    irish: "dobhriathar",
    description: "Describes how, when, or where.",
    Icon: IconClockHour3,
  },
  preposition: {
    label: "preposition",
    irish: "réamhfhocal",
    description: "Shows position or relationship (to, on, with).",
    Icon: IconMapPin,
  },
  pronoun: {
    label: "pronoun",
    irish: "forainm",
    description: "Stands in for a noun (I, you, it).",
    Icon: IconUser,
  },
  conjunction: {
    label: "conjunction",
    irish: "cónasc",
    description: "Joins words or clauses (and, but).",
    Icon: IconLink,
  },
  interjection: {
    label: "interjection",
    irish: "intriacht",
    description: "An exclamation (oh!, hello!).",
    Icon: IconExclamationCircle,
  },
  numeral: {
    label: "numeral",
    irish: "uimhir",
    description: "A number word.",
    Icon: IconNumbers,
  },
  determiner: {
    label: "determiner",
    irish: "alt",
    description: "Introduces a noun (the, this).",
    Icon: IconPointer,
  },
  proper_noun: {
    label: "proper noun",
    irish: "ainmfhocal dílis",
    description: "A specific name: a person, place, brand, or organization.",
    Icon: IconMoodSmile,
  },
}

/**
 * Parts of speech as rich {@link OptionSelect} options — icon + label + Irish name + description — from
 * the same central config, so the sense editor's POS picker teaches what each one is. `order` pins the
 * common ones first; the rest follow in config order.
 */
export const partOfSpeechOptions = (order: string[] = []) => {
  const keys = [
    ...order.filter((k) => PART_OF_SPEECH_META[k]),
    ...Object.keys(PART_OF_SPEECH_META).filter((k) => !order.includes(k)),
  ]
  return keys.map((key) => {
    const meta = PART_OF_SPEECH_META[key]
    return {
      value: key,
      label: meta.label,
      hint: meta.irish,
      description: meta.description,
      icon: (
        <PartOfSpeechIcon
          type={key}
          size={16}
          className="text-muted-foreground"
        />
      ),
    }
  })
}

// Parts of speech grouped by word class, so the picker teaches the categories, not just the terms.
// One place to define which POS belongs to which class + the class blurb.
const POS_CLASSES: Array<{
  label: string
  description: string
  keys: string[]
}> = [
  {
    label: "Content words",
    description: "Carry meaning; the language keeps coining new ones.",
    keys: ["noun", "verb", "adjective", "adverb"],
  },
  {
    label: "Function words",
    description:
      "Structural glue that shows how a sentence fits together. A small, fixed set.",
    keys: [
      "preposition",
      "pronoun",
      "conjunction",
      "determiner",
      "numeral",
      "interjection",
    ],
  },
  // Note: proper nouns are NOT a selectable POS — a name is grammatically a noun. Mark a noun sense as a
  // proper noun via its `entity_type` (name/place/…), which the presenter derives PROPER_NOUN display
  // from. PART_OF_SPEECH_META still carries "proper_noun" so those derived sections render.
]

/**
 * Parts of speech as grouped {@link OptionSelect} groups — word classes (content / function / names),
 * each with a blurb — so the sense editor's POS picker teaches the categories, not just the terms. Built
 * from the same central config. Any POS missing from a class falls into a trailing "Other" group.
 */
export const partOfSpeechGroups = () => {
  const placed = new Set(POS_CLASSES.flatMap((c) => c.keys))
  const groups = POS_CLASSES.map((cls) => ({
    label: cls.label,
    description: cls.description,
    options: cls.keys
      .filter((key) => PART_OF_SPEECH_META[key])
      .map((key) => partOfSpeechOptions([key])[0]),
  }))
  const rest = Object.keys(PART_OF_SPEECH_META).filter(
    (key) => !placed.has(key)
  )
  if (rest.length) {
    groups.push({
      label: "Other",
      description: "",
      options: rest.map((key) => partOfSpeechOptions([key])[0]),
    })
  }
  return groups.filter((g) => g.options.length > 0)
}

/** The Tabler icon for a part of speech (falls back to a generic sense icon for an unknown/unset POS). */
export const partOfSpeechIcon = (type: string): TablerIcon =>
  PART_OF_SPEECH_META[type]?.Icon ?? IconBook2

/**
 * Just the part-of-speech icon (no label/tooltip), from the same central config. Use it to mark a sense
 * anywhere its part of speech is known: the editor tree, list cards, and so on.
 */
export const PartOfSpeechIcon: React.FC<{
  type: string
  size?: number
  className?: string
}> = ({ type, size = 16, className }) => {
  const Icon = partOfSpeechIcon(type)
  return <Icon size={size} className={className} aria-hidden />
}

/**
 * Renders a part of speech as "🏃 verb / briathar" with an icon and a tooltip explaining what it is.
 * The Irish name is shown alongside the English to teach the term.
 */
export const PartOfSpeech: React.FC<{ type: string }> = ({ type, item }) => {
  const meta = PART_OF_SPEECH_META[type]
  if (!meta) return <span className="font-semibold">{type}</span>

  const { label, irish, description, Icon } = meta

  return (
    <HintTooltip
      content={
        <span>
          <span className="font-semibold">
            {label} / {irish}
          </span>
          : {description}
        </span>
      }
    >
      <a
        name={type}
        href={`${item?.path}#${type}`}
        className="inline-flex items-center gap-1 font-semibold"
      >
        <Icon size={16} className="text-muted-foreground" aria-hidden />
        <span>{label}</span>
        <span className="font-normal text-muted-foreground">/ {irish}</span>
      </a>
    </HintTooltip>
  )
}

export default PartOfSpeech
